import {
  Box,
  Button,
  Center,
  Container,
  Divider,
  Flex,
  Loader,
  Paper,
  Tabs,
  Text,
  Title,
  Badge,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { IconArrowLeft, IconUser, IconEye, IconEyeOff, IconChevronDown } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GoalsTab from "./GoalsTab";
import InvestmentsTab from "./InvestmentsTab";
import OrderLogsTab from "./OrderLogsTab";
import ExpenseLogsTab from "./ExpenseLogsTab";
import ReassignUccModal from "./ReassignUccModal";
import { formatINR, formatDate } from "@/shared/utils/format.utils";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import {
  enumLabel,
  GENDER_LABEL,
  OCCUPATION_LABEL,
  WEALTH_SOURCE_LABEL,
  INCOME_SLAB_LABEL,
  POLITICAL_EXPOSURE_LABEL,
  NOMINATION_RELATION_LABEL,
  NOMINEE_IDENTIFIER_LABEL,
  BANK_ACCOUNT_TYPE_LABEL,
  MANDATE_TYPE_LABEL,
  RISK_INVESTMENT_EXPERIENCE_LABEL,
  RISK_MARKET_FLUCTUATIONS_LABEL,
  RISK_LOSS_TAKING_APPETITE_LABEL,
  RISK_PORTFOLIO_DROPS_LABEL,
  RISK_INCOME_STABILITY_LABEL,
  RISK_EMERGENCY_FUNDS_LABEL,
  RISK_FINANCIAL_OBLIGATIONS_LABEL,
  RISK_CURRENT_INVESTMENTS_LABEL,
  RISK_RETURN_EXPECTATIONS_LABEL,
  RISK_INVESTMENT_GOALS_LABEL,
  RISK_TIME_HORIZON_LABEL,
  RISK_TIME_FLEXIBILITY_LABEL,
} from "@/shared/constants/general.const";

// ─── Helpers ────────────────────────────────────────────────────────────────

const calcAge = (dob: Date | string | null | undefined): number | null => {
  if (!dob) return null;
  const today = new Date();
  const d = new Date(dob as string);
  let age = today.getFullYear() - d.getFullYear();
  if (today.getMonth() < d.getMonth() || (today.getMonth() === d.getMonth() && today.getDate() < d.getDate())) {
    age -= 1;
  }
  return age;
};

const maskPan = (pan: string | null | undefined): string => {
  if (!pan) return "N/A";
  if (pan.length < 6) return pan;
  return pan.slice(0, 5) + "****" + pan.slice(-1);
};

const maskAccountNumber = (acNo: string | null | undefined): string => {
  if (!acNo) return "N/A";
  if (acNo.length <= 4) return acNo;
  return (
    "*"
      .repeat(acNo.length - 4)
      .replace(/(.{4})/g, "$1 ")
      .trim() +
    " " +
    acNo.slice(-4)
  );
};

const maskNomineeId = (id: string | null | undefined): string => {
  if (!id) return "N/A";
  if (id.length <= 4) return id;
  return (
    id.slice(0, 4) +
    " " +
    "*"
      .repeat(id.length - 4)
      .replace(/(.{4})/g, "$1 ")
      .trim()
  );
};

// ─── Risk profile Q&A labels ─────────────────────────────────────────────────

const RISK_QUESTIONS: Array<{
  key: string;
  section: 1 | 2 | 3;
  question: string;
  labels: Record<string, string>;
}> = [
  {
    key: "q1InvestmentExperience",
    section: 1,
    question: "How long have you been investing in Equity Markets / Stocks / Mutual Funds?",
    labels: RISK_INVESTMENT_EXPERIENCE_LABEL,
  },
  {
    key: "q2MarketFluctuations",
    section: 1,
    question:
      "How do you usually react to a significant decline in the value of your investments (e.g. 20% to 40% drop)?",
    labels: RISK_MARKET_FLUCTUATIONS_LABEL,
  },
  {
    key: "q3LossTakingAppetite",
    section: 1,
    question: "How comfortable are you with taking risks / losses to achieve better returns?",
    labels: RISK_LOSS_TAKING_APPETITE_LABEL,
  },
  {
    key: "q4PortfolioDrops",
    section: 1,
    question: "What level of temporary decrease in investment portfolio value can you tolerate?",
    labels: RISK_PORTFOLIO_DROPS_LABEL,
  },
  {
    key: "q5IncomeStability",
    section: 2,
    question: "How stable is your current income / job or business?",
    labels: RISK_INCOME_STABILITY_LABEL,
  },
  {
    key: "q6EmergencyFunds",
    section: 2,
    question: "How many months of living expenses have you saved in an Emergency Fund?",
    labels: RISK_EMERGENCY_FUNDS_LABEL,
  },
  {
    key: "q7FinancialObligations",
    section: 2,
    question: "Do you have any near-term financial obligations that you may have to access your investments?",
    labels: RISK_FINANCIAL_OBLIGATIONS_LABEL,
  },
  {
    key: "q8CurrentInvestments",
    section: 2,
    question: "How would you characterise your past and current investments?",
    labels: RISK_CURRENT_INVESTMENTS_LABEL,
  },
  {
    key: "q9ReturnExpectations",
    section: 3,
    question: "What is your expected annual return on your investments to meet your investment objectives?",
    labels: RISK_RETURN_EXPECTATIONS_LABEL,
  },
  {
    key: "q10InvestmentGoals",
    section: 3,
    question: "What is your primary objective for all investments?",
    labels: RISK_INVESTMENT_GOALS_LABEL,
  },
  {
    key: "q11TimeHorizon",
    section: 3,
    question: "How long do you plan to invest before you need to start withdrawing your investments?",
    labels: RISK_TIME_HORIZON_LABEL,
  },
  {
    key: "q12TimeFlexibility",
    section: 3,
    question: "How flexible are you with investment time horizon i.e. how long can you hold the investments?",
    labels: RISK_TIME_FLEXIBILITY_LABEL,
  },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function ProfileField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Flex direction="column" gap={2}>
      <Text fz="xs" c="slate.5">
        {label}
      </Text>
      <Text fz="sm" fw={500}>
        {value ?? "N/A"}
      </Text>
    </Flex>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Flex align="center" gap="xs" mb="md">
      <IconUser size={16} color="var(--mantine-color-slate-5)" />
      <Text fw={500} fz="sm">
        {title}
      </Text>
    </Flex>
  );
}

function CollapsibleCard({
  title,
  children,
  defaultExpanded = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <Paper radius="md" mb="lg" style={{ border: "1px solid #E5E7EB" }}>
      <Flex
        justify="space-between"
        align="center"
        p="lg"
        onClick={() => setExpanded((v) => !v)}
        style={{
          cursor: "pointer",
          borderBottom: expanded ? "1px solid #E5E7EB" : "none",
        }}
      >
        <Text fw={500} fz="md">
          {title}
        </Text>
        <IconChevronDown
          size={20}
          style={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </Flex>
      {expanded && <Box p="lg">{children}</Box>}
    </Paper>
  );
}

// ─── Tab label map ───────────────────────────────────────────────────────────

const TAB_LABELS: Record<string, string> = {
  userDetails: "User details",
  goals: "Goals",
  investments: "Investments",
  orderLogs: "Order logs",
  expenseAnalysis: "Expense logs",
};

// ─── Main component ──────────────────────────────────────────────────────────

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>("userDetails");

  const [reassignUccOpen, setReassignUccOpen] = useState(false);
  const [reassignUccLoading, setReassignUccLoading] = useState(false);

  // Masked-field visibility states
  const [panVisible, setPanVisible] = useState(false);
  const [visibleAccounts, setVisibleAccounts] = useState<Record<string, boolean>>({});
  const [visibleNomineeIds, setVisibleNomineeIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!id) return;
    const fetchUser = async () => {
      setLoading(true);
      try {
        const result = await trpc.users.getById.query({ id });
        setUser(result.data);
      } catch (err) {
        handleTrpcError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  if (!user) {
    return (
      <Container size="fluid" py="xl">
        <Text>User not found</Text>
      </Container>
    );
  }

  const fullName = [user.firstName, user.middleName, user.lastName].filter(Boolean).join(" ");
  const fullPhone = user.contactNumber ? `${user.countryCode ?? ""} ${user.contactNumber}`.trim() : null;
  const fullAddress = [user.addressLine1, user.addressLine2, user.addressLine3, user.city, user.state]
    .filter(Boolean)
    .join(", ");

  const nominees: any[] = user.nominee ?? [];
  const bankAccounts: any[] = user.bankAccounts ?? [];
  const mandates: any[] = user.uccAccount?.mandates ?? [];
  const taxResidencies: any[] = user.taxResidency ?? [];
  const riskProfile = user.investorRiskProfile ?? null;

  return (
    <Box style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* ── Page Header ──────────────────────────────────────────────── */}
      <Box px="xl" pt="lg" pb={0} style={{ flexShrink: 0 }}>
        <Flex justify="space-between" align="flex-start">
          {/* Left: back + name + PAN badge + stats */}
          <Flex direction="column" gap={6}>
            <Flex align="center" gap="sm">
              <ActionIcon variant="subtle" color="gray" onClick={() => navigate("/home")}>
                <IconArrowLeft size={20} />
              </ActionIcon>
              <Title order={2} fw={700}>
                {fullName}
              </Title>
              {user.panVerified && (
                <Badge variant="light" color="teal" size="sm" radius="sm">
                  PAN verified
                </Badge>
              )}
            </Flex>

            {/* Portfolio stats row */}
            <Flex align="center" gap="xs" pl={36}>
              <Text fz="sm" c="slate.6">
                Total Invested:
              </Text>
              <Text fz="sm" fw={500}>
                0
              </Text>
              <Text c="slate.4" fz="sm">
                |
              </Text>
              <Text fz="sm" c="slate.6">
                Current Value:
              </Text>
              <Text fz="sm" fw={500} c="green.7">
                0
              </Text>
              <Text c="slate.4" fz="sm">
                |
              </Text>
              <Text fz="sm" c="slate.6">
                XIRR:
              </Text>
              <Text fz="sm" fw={500} c="green.7">
                0
              </Text>
            </Flex>
          </Flex>

          {/* Right: Reassign UCC */}
          {(() => {
            const isUccActive = user.uccAccount?.status === "ACTIVE";
            const button = (
              <Button
                variant="filled"
                color="primary"
                onClick={() => setReassignUccOpen(true)}
                disabled={isUccActive}
              >
                Reassign UCC
              </Button>
            );
            return isUccActive ? (
              <Tooltip label="UCC is active and cannot be reassigned" withArrow>
                <span>{button}</span>
              </Tooltip>
            ) : (
              button
            );
          })()}
        </Flex>
      </Box>

      {/* ── Tabs ─────────────────────────────────────────────────────── */}
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Box px="xl" mt="lg" style={{ flexShrink: 0 }}>
          <Tabs.List>
            {Object.entries(TAB_LABELS).map(([value, label]) => (
              <Tabs.Tab
                key={value}
                value={value}
                px="md"
                style={{
                  color: activeTab === value ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-6)",
                }}
              >
                {label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Box>

        {/* ── User Details Tab ───────────────────────────────────────── */}
        <Tabs.Panel value="userDetails" pt="lg" style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          <Container size="fluid" px="xl" pb="xl">
            {/* ── Profile Details ─────────────────────────── */}
            <CollapsibleCard title="Profile Details">
              {/* Basic Details */}
              <SectionHeader title="Basic Details" />
              <Box style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1.5rem" }} mb="xl">
                <Flex direction="column" gap={2}>
                  <Text fz="xs" c="slate.5">
                    Pan ID
                  </Text>
                  <Flex align="center" gap={6}>
                    <Text fz="sm" fw={500}>
                      {panVisible ? (user.panNumber ?? "N/A") : maskPan(user.panNumber)}
                    </Text>
                    <ActionIcon variant="subtle" color="gray" size="xs" onClick={() => setPanVisible((v) => !v)}>
                      {panVisible ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                    </ActionIcon>
                  </Flex>
                </Flex>
                <ProfileField label="Email ID" value={user.email} />
                <ProfileField label="UCC Number" value={user.uccAccount?.clientCode ?? null} />
                <ProfileField label="Mobile no" value={fullPhone} />
                <ProfileField label="Date of birth" value={formatDate(user.dateOfBirth)} />
                <ProfileField label="Age" value={user.age != null ? `${user.age} Yrs` : null} />
                <ProfileField label="Gender" value={enumLabel(GENDER_LABEL, user.gender)} />
              </Box>

              <Divider mb="xl" />

              {/* Personal Details */}
              <SectionHeader title="Personal Details" />
              <Box style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1.5rem" }} mb="xl">
                <ProfileField label="Place of birth" value={user.placeOfBirth} />
                <ProfileField label="Country of birth" value={user.countryOfBirth} />
                <ProfileField label="Name of father" value={user.fatherName} />
                <ProfileField label="Name of spouse" value={user.spouseName || "N/A"} />
              </Box>

              <Divider mb="xl" />

              {/* Address Details */}
              <SectionHeader title="Address Details" />
              <Box style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1.5rem" }} mb="xl">
                <ProfileField label="Country of residence" value={user.country} />
                <ProfileField label="Postal code" value={user.postalCode} />
                <ProfileField label="Address" value={fullAddress || null} />
              </Box>

              <Divider mb="xl" />

              {/* Tax Information */}
              <SectionHeader title="Tax Information" />
              <Box style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1.5rem" }}>
                <ProfileField label="Tax status" value={user.taxStatus?.taxStatus ?? null} />
                <ProfileField label="Tax residency country" value={taxResidencies[0]?.country ?? null} />
                <ProfileField label="Occupation" value={enumLabel(OCCUPATION_LABEL, user.occupation)} />
                <ProfileField label="Wealth source" value={enumLabel(WEALTH_SOURCE_LABEL, user.wealthSource)} />
                <ProfileField label="Income slab" value={enumLabel(INCOME_SLAB_LABEL, user.incomeSlab)} />
                <ProfileField
                  label="Politically exposed"
                  value={enumLabel(POLITICAL_EXPOSURE_LABEL, user.politicallyExposed)}
                />
              </Box>
            </CollapsibleCard>

            {/* ── Nominee Details ─────────────────────────── */}
            {nominees.length > 0 && (
              <CollapsibleCard title="Nominee Details">
                {nominees.map((nominee: any, idx: number) => (
                  <Box key={nominee.id ?? idx}>
                    {idx > 0 && <Divider my="xl" />}

                    {/* Basic Details */}
                    <SectionHeader title="Basic Details" />
                    <Box
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "1.5rem",
                      }}
                      mb="xl"
                    >
                      <ProfileField
                        label="Name"
                        value={[nominee.firstName, nominee.middleName, nominee.lastName].filter(Boolean).join(" ")}
                      />
                      <ProfileField label="Email ID" value={nominee.email} />
                      <ProfileField
                        label="Mobile no"
                        value={
                          nominee.contactNumber ? `${nominee.countryCode ?? ""} ${nominee.contactNumber}`.trim() : null
                        }
                      />
                      <ProfileField
                        label="Age"
                        value={calcAge(nominee.dateOfBirth) != null ? `${calcAge(nominee.dateOfBirth)} Yrs` : null}
                      />
                      <ProfileField label="Date of birth" value={formatDate(nominee.dateOfBirth)} />
                      <ProfileField label="Gender" value={enumLabel(GENDER_LABEL, nominee.gender)} />
                    </Box>

                    <Divider mb="xl" />

                    {/* Eligibility Details */}
                    <SectionHeader title="Eligibility Details" />
                    <Box
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "1.5rem",
                      }}
                      mb="xl"
                    >
                      <ProfileField
                        label="Nominee relationship"
                        value={enumLabel(NOMINATION_RELATION_LABEL, nominee.nominationRelation)}
                      />
                      <ProfileField
                        label="Is nominee a minor?"
                        value={nominee.nomineeIsMinor === true ? "Yes" : nominee.nomineeIsMinor === false ? "No" : null}
                      />
                      <ProfileField
                        label="Document type"
                        value={enumLabel(NOMINEE_IDENTIFIER_LABEL, nominee.nomineeIdentifierType)}
                      />
                      {/* Masked nominee identifier number */}
                      <Flex direction="column" gap={2}>
                        <Text fz="xs" c="slate.5">
                          Document ID
                        </Text>
                        <Flex align="center" gap={6}>
                          <Text fz="sm" fw={500}>
                            {visibleNomineeIds[nominee.id]
                              ? (nominee.nomineeIdentifierNumber ?? "N/A")
                              : maskNomineeId(nominee.nomineeIdentifierNumber)}
                          </Text>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="xs"
                            onClick={() =>
                              setVisibleNomineeIds((prev) => ({
                                ...prev,
                                [nominee.id]: !prev[nominee.id],
                              }))
                            }
                          >
                            {visibleNomineeIds[nominee.id] ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                          </ActionIcon>
                        </Flex>
                      </Flex>
                    </Box>

                    {/* Guardian Details */}
                    <Divider mb="xl" />
                    <SectionHeader title="Guardian Details" />
                    <Box
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "1.5rem",
                      }}
                    >
                      <ProfileField
                        label="Name"
                        value={
                          nominee.guardian
                            ? [nominee.guardian.firstName, nominee.guardian.middleName, nominee.guardian.lastName]
                                .filter(Boolean)
                                .join(" ")
                            : "----"
                        }
                      />
                      <ProfileField label="Document type" value={nominee.guardian?.guardianIdentifier ?? "----"} />
                      <ProfileField
                        label="Document ID number"
                        value={nominee.guardian?.guardianIdentifierNumber ?? "----"}
                      />
                    </Box>
                  </Box>
                ))}
              </CollapsibleCard>
            )}

            {/* ── Bank & Mandate Details ──────────────────── */}
            {(bankAccounts.length > 0 || mandates.length > 0) && (
              <CollapsibleCard title="Bank & Mandate Details">
                {/* Bank Details */}
                {bankAccounts.length > 0 && (
                  <>
                    <SectionHeader title="Bank Details" />
                    {bankAccounts.map((account: any, idx: number) => (
                      <Box key={account.id ?? idx} mb={idx < bankAccounts.length - 1 ? "xl" : 0}>
                        <Box
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            gap: "1.5rem",
                          }}
                        >
                          <ProfileField label="Bank Name" value={null} />
                          {/* Masked A/C number */}
                          <Flex direction="column" gap={2}>
                            <Text fz="xs" c="slate.5">
                              A/C No
                            </Text>
                            <Flex align="center" gap={6}>
                              <Text fz="sm" fw={500}>
                                {visibleAccounts[account.id]
                                  ? (account.bankAccountNumber ?? "N/A")
                                  : maskAccountNumber(account.bankAccountNumber)}
                              </Text>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="xs"
                                onClick={() =>
                                  setVisibleAccounts((prev) => ({
                                    ...prev,
                                    [account.id]: !prev[account.id],
                                  }))
                                }
                              >
                                {visibleAccounts[account.id] ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                              </ActionIcon>
                            </Flex>
                          </Flex>
                          <ProfileField label="IFSC" value={account.ifscCode} />
                          <ProfileField
                            label="Account type"
                            value={enumLabel(BANK_ACCOUNT_TYPE_LABEL, account.bankAccountType)}
                          />
                        </Box>
                      </Box>
                    ))}
                  </>
                )}

                {/* Mandate Details */}
                {mandates.length > 0 && (
                  <>
                    {bankAccounts.length > 0 && <Divider my="xl" />}
                    <SectionHeader title="Mandate Details" />
                    <Flex direction="column" gap="md">
                      {mandates.map((mandate: any, idx: number) => (
                        <Box
                          key={mandate.id ?? idx}
                          style={{
                            border: "1px solid #E5E7EB",
                            borderRadius: 8,
                            overflow: "hidden",
                          }}
                        >
                          {/* Mandate header */}
                          <Box px="md" py="sm" style={{ backgroundColor: "#F5F2FE" }}>
                            <Text fz="sm" fw={600} c="primary.7">
                              OTM Limit: {formatINR(mandate.amount)}{" "}
                              <Text component="span" fw={400} c="slate.6">
                                ({MANDATE_TYPE_LABEL[mandate.type] ?? mandate.type})
                              </Text>
                            </Text>
                          </Box>
                          {/* Mandate body */}
                          <Box
                            px="md"
                            py="sm"
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(4, 1fr)",
                              gap: "1rem",
                            }}
                          >
                            <ProfileField label="Start date" value={formatDate(mandate.startDate)} />
                            <ProfileField label="End date" value={formatDate(mandate.validTill)} />
                            <ProfileField label="eMandate Period" value="Until Cancelled" />
                            <ProfileField label="Mandate frequency" value={mandate.frequency} />
                          </Box>
                        </Box>
                      ))}
                    </Flex>
                  </>
                )}
              </CollapsibleCard>
            )}

            {/* ── Risk Profile ────────────────────────────── */}
            {riskProfile && (
              <CollapsibleCard title="Risk Profile">
                {([1, 2, 3] as const).map((section, sIdx) => {
                  const sectionLabels: Record<number, string> = {
                    1: "Section 1: Risk Tolerance",
                    2: "Section 2: Risk Capacity",
                    3: "Section 3: Risk Required",
                  };
                  const qs = RISK_QUESTIONS.filter((q) => q.section === section);
                  return (
                    <Box key={section}>
                      {sIdx > 0 && <Divider mb="xl" />}
                      <SectionHeader title={sectionLabels[section]} />
                      <Box
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: "1.5rem",
                        }}
                        mb="xl"
                      >
                        {qs.map((q) => {
                          const rawValue = riskProfile[q.key];
                          const displayValue = rawValue ? (q.labels[rawValue] ?? rawValue) : null;
                          return (
                            <Flex key={q.key} direction="column" gap={2}>
                              <Text fz="xs" c="slate.5">
                                {q.question}
                              </Text>
                              <Text fz="sm" fw={500}>
                                {displayValue ?? "N/A"}
                              </Text>
                            </Flex>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </CollapsibleCard>
            )}
          </Container>
        </Tabs.Panel>

        <Tabs.Panel value="goals" pt="lg" style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
          <Container size="fluid" px="xl" pb="xl">
            <GoalsTab goals={user.goals ?? []} orders={user.orders ?? []} sxps={user.sxps ?? []} />
          </Container>
        </Tabs.Panel>

        <Tabs.Panel value="investments" pt="lg" style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
          <Container size="fluid" px="xl" pb="xl">
            <InvestmentsTab orders={user.orders ?? []} sxps={user.sxps ?? []} />
          </Container>
        </Tabs.Panel>

        <Tabs.Panel value="orderLogs" pt="lg" style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
          <Container size="fluid" px="xl" pb="xl">
            <OrderLogsTab orders={user.orders ?? []} sxps={user.sxps ?? []} />
          </Container>
        </Tabs.Panel>

        <Tabs.Panel value="expenseAnalysis" pt="lg" style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
          <Container size="fluid" px="xl" pb="xl">
            <ExpenseLogsTab
              expenseEntries={user.expenseTrackerEntries ?? []}
              expenseCategories={user.expenseTrackerCategories ?? []}
            />
          </Container>
        </Tabs.Panel>
      </Tabs>

      <ReassignUccModal
        opened={reassignUccOpen}
        onClose={() => setReassignUccOpen(false)}
        isLoading={reassignUccLoading}
        onConfirm={async (step) => {
          if (!id) return;
          setReassignUccLoading(true);
          try {
            await trpc.users.resetUccOnboardingStep.mutate({ userId: id, resetTo: step });
            successNotification({ title: "Reset Successful", message: "UCC onboarding step reset successfully." });
            setReassignUccOpen(false);
          } catch (err) {
            handleTrpcError(err);
          } finally {
            setReassignUccLoading(false);
          }
        }}
      />
    </Box>
  );
}
